import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { TwoFactorChallengeResponse } from '@/shared/api';
import { dashboardLanding, useAuth } from '@/shared/auth';
import { AuthPageHeader } from './AuthPageHeader';
import { loginFailure, type AuthFailure } from './loginFailure';
import { LoginPasswordForm, type LoginValues } from './LoginPasswordForm';
import { loginWorkspacePrefill } from './loginPrefill';
import { LoginRequiredTwoFactorSetup } from './LoginRequiredTwoFactorSetup';
import { LoginTwoFactorForm } from './LoginTwoFactorForm';

/** Anmeldung (Board A): Passwort, dann bei Bedarf Code oder Pflicht-Einrichtung der 2FA. */
export function LoginPage({ embedded = false }: { embedded?: boolean }) {
  const { login, completeTwoFactor, status, user } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { workspace?: unknown };
  const workspace = loginWorkspacePrefill(search.workspace);
  const [challenge, setChallenge] = useState<TwoFactorChallengeResponse | null>(null);
  const [failure, setFailure] = useState<AuthFailure | null>(null);

  useEffect(() => {
    if (!embedded && status === 'authenticated' && user) {
      void navigate({ to: dashboardLanding(user) ?? '/' });
    }
  }, [embedded, navigate, status, user]);

  const submitPassword = async (values: LoginValues) => {
    setFailure(null);
    try {
      const outcome = await login(values.tenantSlug, values.email, values.password);
      if (outcome.status === 'success') {
        toast.success('Willkommen zurück');
        return;
      }
      setChallenge(outcome);
    } catch (error) {
      setFailure(await loginFailure(error));
    }
  };

  const complete = async (code: string, useRecoveryCode: boolean) => {
    if (!challenge || challenge.status !== 'twoFactorRequired') return;
    await completeTwoFactor(challenge.challengeToken, code, useRecoveryCode);
    toast.success('Willkommen zurück');
  };
  const back = () => {
    setChallenge(null);
    setFailure(null);
  };

  if (challenge?.status === 'twoFactorSetupRequired')
    return <LoginRequiredTwoFactorSetup challenge={challenge} onBack={back} />;
  if (challenge)
    return (
      <>
        <AuthPageHeader
          eyebrow="Sicherheitsprüfung"
          title="Code eingeben"
          description="Die zweite Stufe schützt dein Konto."
        />
        <LoginTwoFactorForm onBack={back} onComplete={complete} />
      </>
    );
  return (
    <>
      <AuthPageHeader eyebrow="Anmelden" title="Bei StayOn anmelden" />
      <LoginPasswordForm
        onSubmit={submitPassword}
        failure={failure}
        defaultTenantSlug={workspace}
      />
    </>
  );
}
